/**
 * Faculty, clock and seating commands (DESIGN §5.1, §10.6).
 */
import type { ControlCommand } from '../commands.ts';
import { eventCard } from '../content/events.ts';
import { diag } from '../content/compendium.ts';
import { claimSeat, dealObjectives, invalidateSeatCommitments, releaseSeat, startShift } from '../setup.ts';
import type { PatientId, Role } from '../types.ts';
import { clockLength, emit, ensure, place, reject, table, type Ctx } from './common.ts';
import { applyEvent, arriveAtEmergency } from './events.ts';
import { advancePhase, enterDebrief, phaseDurationMs, setPhase } from './phases.ts';
import { ventsFree } from './act.ts';

/** Apply explicit seat controller changes. */
function runSeatControl(ctx: Ctx, c: ControlCommand): boolean {
  switch (c.type) {
    case 'SEAT_CLAIM':
      claimSeat(ctx, c.table, c.role, c.name, c.bot);
      return true;
    case 'SEAT_RELEASE':
      releaseSeat(ctx, c.table, c.role);
      return true;
    case 'SET_BOT': {
      const t = table(ctx.s, c.table);
      const st = t.seats[c.role];
      ensure(st, 'Nobody in that seat.');
      if (st.bot !== c.bot) invalidateSeatCommitments(t, c.role);
      st.bot = c.bot;
      if (c.bot && !st.name) st.name = `Bot ${c.role}`;
      emit(ctx, { type: 'SEAT_BOT', table: c.table, seat: c.role, text: `${c.role} at table ${c.table} is now ${c.bot ? 'a bot' : 'a person'}.` });
      return true;
    }
    default: return false;
  }
}

/** Start or advance one public boundary. */
function runBoundaryControl(ctx: Ctx, c: ControlCommand): boolean {
  const s = ctx.s;
  const clock = s.clock;
  switch (c.type) {
    case 'START_SHIFT':
      startShift(ctx);
      advancePhase(ctx);
      for (const t of s.tables) dealObjectives(s, t, ctx.rng);
      return true;
    case 'ADVANCE_PHASE':
      if (c.from.turn !== clock.turn || c.from.phase !== clock.phase) reject('The turn has already moved on. Try again.');
      if (clock.phase === 'LOG' && ctx.actor.kind !== 'faculty') reject('Only Control can start the next turn.');
      if (ctx.actor.kind === 'clock' && (clock.paused || clock.hold)) reject('The clock is paused or held.');
      ensure(clock.phase !== 'SETUP', 'Use Start shift.');
      ensure(clock.phase !== 'ENDED', 'The session has ended.');
      advancePhase(ctx);
      return true;
    default: return false;
  }
}

/** Preserve pause, extension and held-clock behavior. */
function runClockControl(ctx: Ctx, c: ControlCommand): boolean {
  const s = ctx.s;
  const clock = s.clock;
  switch (c.type) {
    case 'PAUSE':
      if (clock.paused) return true;
      clock.paused = true;
      clock.remainingMs = clock.phaseEndsAt !== null ? Math.max(0, clock.phaseEndsAt - ctx.at) : null;
      clock.phaseEndsAt = null;
      emit(ctx, { type: 'PAUSED', text: 'Control paused the room.', audience: ['faculty', 'table'] });
      return true;
    case 'RESUME': {
      if (!clock.paused) return true;
      clock.paused = false;
      const d = clock.remainingMs ?? phaseDurationMs(s);
      clock.phaseEndsAt = d === null || clock.hold ? null : ctx.at + d;
      clock.remainingMs = null;
      emit(ctx, { type: 'RESUMED', text: 'Control resumed the room.', audience: ['faculty', 'table'] });
      return true;
    }
    case 'EXTEND':
      if (clock.paused && clock.remainingMs !== null) clock.remainingMs += c.ms;
      else if (clock.phaseEndsAt !== null) clock.phaseEndsAt += c.ms;
      emit(ctx, { type: 'EXTENDED', text: `Control added ${Math.round(c.ms / 1000)} s.`, audience: ['faculty', 'table'] });
      return true;
    case 'HOLD':
      clock.hold = c.on;
      if (!c.on && clock.phaseEndsAt === null && !clock.paused) {
        const d = phaseDurationMs(s);
        clock.phaseEndsAt = d === null ? null : ctx.at + Math.min(d, 10000);
      }
      return true;
    default: return false;
  }
}

/** Apply room interventions without advancing care implicitly. */
function runRoomControl(ctx: Ctx, c: ControlCommand): boolean {
  const s = ctx.s;
  const clock = s.clock;
  switch (c.type) {
    case 'INJECT': {
      eventCard(c.event);
      if (c.when === 'now' && clock.phase === 'ARRIVALS') {
        const extras = new Map<number, PatientId[]>();
        applyEvent(ctx, c.event, extras, new Map());
        for (const t of s.tables) for (const id of extras.get(t.no) ?? []) arriveAtEmergency(ctx, t, s.patients[id]!);
      } else {
        s.room.queuedInjects.push(c.event);
        emit(ctx, { type: 'INJECT_QUEUED', text: `${c.event} queued for next turn.` });
      }
      return true;
    }
    case 'BROADCAST':
      s.room.broadcast = c.text;
      emit(ctx, { type: 'BROADCAST', text: c.text ?? '(cleared)', audience: ['faculty', 'table'] });
      return true;
    case 'RESTOCK': {
      const t = table(s, c.table);
      if (c.resource === 'staff') {
        const r: Role = c.role ?? t.lead;
        ensure(t.seats[r], 'Nobody in that seat.');
        t.seats[r]!.staff = Math.max(0, t.seats[r]!.staff + c.n);
      } else if (c.resource === 'vents') {
        ensure(t.board.vents, 'The ventilator layer is off.');
        t.board.vents!.total = Math.max(0, t.board.vents!.total + c.n);
      } else {
        ensure(t.board.blood, 'The blood layer is off.');
        t.board.blood!.units = Math.max(0, Math.min(12, t.board.blood!.units + c.n));
      }
      emit(ctx, { type: 'RESTOCK', table: t.no, text: `Control changed ${c.resource} at table ${t.no} by ${c.n}.` });
      return true;
    }
    case 'END_SHIFT':
      if (clock.phase === 'DEBRIEF') {
        setPhase(ctx, 'ENDED');
        return true;
      }
      ensure(clock.phase !== 'ENDED' && clock.phase !== 'SETUP', 'Nothing to end.');
      setPhase(ctx, 'DEBRIEF');
      enterDebrief(ctx, true);
      return true;
    case 'SET_RING':
      ensure(c.order.length === s.tables.length && new Set(c.order).size === c.order.length && c.order.every((n) => s.tables.some((t) => t.no === n)), 'The ring must list every table once.');
      s.config.ringOrder = c.order;
      return true;
    default: return false;
  }
}

/** Apply audited patient and resource corrections. */
function runCorrectionControl(ctx: Ctx, c: ControlCommand): boolean {
  const s = ctx.s;
  switch (c.type) {
    case 'CORRECT_RESTORE_ACTION': {
      const st = table(s, c.table).seats[c.role];
      ensure(st, 'Nobody in that seat.');
      st.actions.available += c.n;
      emit(ctx, { type: 'CORRECTION', table: c.table, seat: c.role, severity: 1, text: `Control restored ${c.n} action(s) to ${c.role}: ${c.reason}` });
      return true;
    }
    case 'CORRECT_MOVE': {
      const p = s.patients[c.p];
      ensure(p && (p.status === 'ACTIVE' || p.status === 'INCOMING'), 'That patient is not active or incoming.');
      const t = table(s, p.table);
      p.flags.ventHeld = false;
      place(s, t, c.role, p, c.bed);
      p.flags.ventHeld = c.role === 'ICU' && !!t.board.vents && diag(p.believedKey).vent && ventsFree(s, t) > 0;
      for (const transfer of t.pendingTransfers) if (transfer.patient === p.id) transfer.to = c.role;
      emit(ctx, { type: 'CORRECTION', table: t.no, seat: c.role, patient: p.id, severity: 1, text: `Control moved ${p.name} to ${c.role}: ${c.reason}` });
      return true;
    }
    case 'CORRECT_CLOCK': {
      const p = s.patients[c.p];
      ensure(p, 'No such patient.');
      p.ticks = Math.min(c.ticks, clockLength(p.colour) - 1);
      emit(ctx, { type: 'CORRECTION', table: p.table, patient: p.id, severity: 1, text: `Control set ${p.name}'s clock to ${p.ticks}: ${c.reason}` });
      return true;
    }
    case 'CORRECT_PROMPT': {
      const t = table(s, c.table);
      const q = t.prompts.find((x) => x.id === c.prompt);
      ensure(q, 'No such prompt.');
      t.prompts = t.prompts.filter((x) => x.id !== q.id);
      emit(ctx, { type: 'CORRECTION', table: t.no, severity: 1, text: `Control answered a prompt: ${c.reason}` });
      return true;
    }
    default: return false;
  }
}

export function runControl(ctx: Ctx, c: ControlCommand): void {
  if (runSeatControl(ctx, c) || runBoundaryControl(ctx, c) || runClockControl(ctx, c)
    || runRoomControl(ctx, c) || runCorrectionControl(ctx, c)) return;
}
